import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Task } from '@/types';
import { api } from '@/services/api';
import PageLayout from '@/shared/ui/layout/PageLayout';
import GroupedContent from '@/shared/ui/layout/GroupedContent';
import { LinkCard, CardTitle, CardDescription, CardSection } from '@/shared/ui/card';
import './styles.css';

const TaskList: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTasks = async () => {
    try {
      const response = await api.tasks.getAll();
      setTasks(response);
      
      // Extract unique categories
      const uniqueCategories = Array.from(new Set(response.map(task => task.category)));
      setCategories(uniqueCategories);
      
      setError(null);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await api.tasks.delete(id);
        setTasks(tasks.filter(task => task._id !== id));
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to delete task');
        }
      }
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const groupTasksByCategory = (tasks: Task[]) => {
    const filteredTasks = selectedCategory === 'all' 
      ? tasks 
      : tasks.filter(task => task.category === selectedCategory);

    return filteredTasks.reduce((acc, task) => {
      const category = task.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(task);
      return acc;
    }, {} as Record<string, Task[]>);
  };

  const renderTaskGroup = (category: string, tasks: Task[]) => (
    <div className="tasks-grid">
      {tasks.map(task => (
        <LinkCard 
          key={task._id} 
          to={`/tasks/${task._id}`}
        >
          <CardTitle title={task.title} />
          <CardDescription maxLines={2}>
            {task.content}
          </CardDescription>
          <CardSection>
            <CardDescription variant="meta">
              <div className="status-badge" data-status={task.scoringMethod}>
                {task.scoringMethod === 'numerical' ? 'Numerical Score' : 'Pass/Fail'}
              </div>
              {task.scoringMethod === 'numerical' && task.scoringScale && (
                <span className="scoring-scale">
                  Scale: {task.scoringScale.min} - {task.scoringScale.max}
                </span>
              )}
            </CardDescription>
          </CardSection>
        </LinkCard>
      ))}
    </div>
  );

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  const groupedTasks = groupTasksByCategory(tasks);

  return (
    <PageLayout
      title="Tasks"
      actions={
        <Link to="/tasks/new" className="button">Create</Link>
      }
      headerContent={
        <div className="category-filter">
          <button 
            className={`category-button ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
          >
            All Categories
          </button>
          {categories.map(category => (
            <button 
              key={category}
              className={`category-button ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category)}
            >
              {category}
            </button>
          ))}
        </div>
      }
    >
      {Object.keys(groupedTasks).length === 0 ? (
        <div className="empty-message">No tasks found. Create your first task!</div>
      ) : (
        <GroupedContent
          groups={groupedTasks}
          renderGroup={renderTaskGroup}
          emptyMessage="No tasks found. Create your first task!"
        />
      )}
    </PageLayout>
  );
};

export default TaskList; 